/* eslint-disable max-len */
import { InfoIcon } from '@phosphor-icons/react';
import {
  Divider, Flex, Table, Tabs,
  Tooltip
} from 'antd';
import { ColumnsType, TableProps } from 'antd/es/table';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Typography } from 'tfc-components';

import FileInChatbot from 'components/molecules/FileInChatbot';
import SearchBar from 'components/molecules/SearchBar';
import { TCleanRequire } from 'pages/ChangePlan';
import { getStorageAgentsService } from 'services/agents';
import { StorageAgentDataType } from 'services/agents/types';
import { bytesToMB } from 'utils/functions';

interface Props {
  cleanRequire: TCleanRequire;
  setCleanRequire: (data: TCleanRequire) => void;
}

const CleanData: React.FC<Props> = ({ cleanRequire, setCleanRequire }) => {
  /* State */
  const [activeTab, setActiveTab] = useState('1');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchTxt, setSearchTxt] = useState('');
  const [disabledAgents, setDisabledAgents] = useState<string[]>([]);

  /* Queries */
  const queryKey = ['agents-list'];
  const { data: dataAgent } = useQuery(
    queryKey,
    () => getStorageAgentsService(),
  );

  const columnsAgent: ColumnsType<StorageAgentDataType> = [
    {
      title: (
        <Typography.Text className="t-accountList_table-head">
          Name
        </Typography.Text>
      ),
      dataIndex: 'name',
      key: 'agentData.name',
      render: (_name: string, data: StorageAgentDataType) => (
        <Typography.Text>
          {data.name}
        </Typography.Text>
      ),
    },
  ];

  const rowSelection: TableProps<StorageAgentDataType>['rowSelection'] = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      // // agents bị tick → disable file/web
      const tickedAgents = rows.map((r) => r.code);
      setDisabledAgents(tickedAgents);
      setSelectedRowKeys(keys);

      const totalFile = tickedAgents.reduce((sum, code) => {
        const agent = dataAgent?.find((a) => a.code === code);
        return sum + (agent?.ttlFile || 0);
      }, 0);

      const totalWeb = tickedAgents.reduce((sum, code) => {
        const agent = dataAgent?.find((a) => a.code === code);
        return sum + (agent?.ttlWeb || 0);
      }, 0);

      const totalStorage = tickedAgents.reduce((sum, code) => {
        const agent = dataAgent?.find((a) => a.code === code);
        return sum + bytesToMB(agent?.ttlSizeByte || 0);
      }, 0);

      // Cập nhật sạch
      setCleanRequire({
        ...cleanRequire,

        chatbot: {
          ...cleanRequire.chatbot,
          cleaned: {
            ...cleanRequire.chatbot.cleaned,
            // cleaned = số agent đang tick
            total: tickedAgents.length
          },
          code: {
            ...cleanRequire.chatbot.code,
            total: tickedAgents
          }
        },

        file: {
          ...cleanRequire.file,
          cleaned: {
            // ...cleanRequire.file.cleaned,
            total: totalFile
          }
        },

        web: {
          ...cleanRequire.web,
          cleaned: {
            // ...cleanRequire.web.cleaned,
            total: totalWeb
          }
        },

        storage: {
          ...cleanRequire.storage,
          cleaned: {
            // ...cleanRequire.storage.cleaned,
            total: totalStorage
          }
        }
      });
    },
  };
  const components = {
    header: {
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (props: any) => (
        <th
          {...props}
          style={{
            backgroundColor: 'white',
            fontWeight: 'bold',
          }}
        />
      ),
    },
  };

  const dataChatbot = useMemo(() => {
    let data;
    if (searchTxt) {
      data = dataAgent?.filter(
        (item) => item.name?.toLocaleLowerCase().includes(searchTxt.toLocaleLowerCase())
      );
    } else {
      data = dataAgent;
    }
    return data;
  }, [dataAgent, searchTxt]);

  const dataList = [
    {
      id: '1',
      labelText: 'Chatbot',
      content: (
        <div className="o-cleanData_listChatbot">
          <SearchBar onChange={(txt) => setSearchTxt(txt)} />
          <Table
            rowKey={(props) => props?.code}
            dataSource={dataChatbot}
            columns={columnsAgent}
            rowSelection={rowSelection}
            components={components}
          />
        </div>
      )

    },
    {
      id: '2',
      labelText: 'Files',
      content: <FileInChatbot
        setCleanRequire={setCleanRequire}
        sourceType={2}
        dataAgent={dataAgent || []}
        cleanRequire={cleanRequire}
        disabledAgents={disabledAgents}
      />
    },
    {
      id: '3',
      labelText: 'Website',
      content: <FileInChatbot
        setCleanRequire={setCleanRequire}
        sourceType={1}
        dataAgent={dataAgent || []}
        cleanRequire={cleanRequire}
        disabledAgents={disabledAgents}
      />,
    },
  ];

  useEffect(() => {
    if (dataChatbot?.length && cleanRequire.chatbot.code?.length) {
      // setSelectedRowKeys(cleanRequire.chatbot.code);

    }
  }, [cleanRequire.chatbot.code, dataChatbot]);

  const totalFileCleaned = useMemo(() => Object.values(cleanRequire.file.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.file.cleaned]);

  const totalWebCleaned = useMemo(() => Object.values(cleanRequire.web.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.web.cleaned]);

  const totalStorageCleaned = useMemo(() => Object.values(cleanRequire.storage.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.storage.cleaned]);

  const totalChatbotCleaned = useMemo(() => Object.values(cleanRequire.chatbot.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.chatbot.cleaned]);

  return (
    <div className="p-changePlan_cleanData o-cleanData">
      <Typography.Text extendClasses="fs-20x24" fontweight="600">
        Cleanup require
        {' '}
        <Tooltip className="o-cleanData_tooltip" overlayInnerStyle={{ width: 528 }} title="Deleting a chatbot will permanently delete all related data. This action cannot be undone.">
          <InfoIcon size={24} />
        </Tooltip>
      </Typography.Text>

      <Flex gap={24} className="p-changePlan_cleanupRequire">
        <Flex vertical flex={1} gap={8}>
          <Typography.Text>Number of chatbot</Typography.Text>
          <Flex gap={16}>
            <Flex flex={1} vertical>
              <Typography.Text extendClasses="fs-28x33" fontweight="600">{cleanRequire.chatbot.needToClean}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Need to cleanup</Typography.Text>
            </Flex>

            <Divider type="vertical" style={{ height: '100%' }} />
            <Flex flex={1} vertical>
              <Typography.Text extendClasses={(`fs-28x33 p-changePlan_cleanData${cleanRequire.chatbot.needToClean <= totalChatbotCleaned ? '-cleaned' : '-notclean'}`)} fontweight="600">{totalChatbotCleaned}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Cleaned</Typography.Text>
            </Flex>
          </Flex>
        </Flex>

        <Divider type="vertical" style={{ height: 'auto' }} />

        <Flex vertical flex={1} gap={8}>
          <Typography.Text>Storage (MB)</Typography.Text>
          <Flex gap={16}>
            <Flex flex={1} vertical>
              <Typography.Text extendClasses="fs-28x33" fontweight="600">{cleanRequire.storage.needToClean}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Need to cleanup</Typography.Text>
            </Flex>

            <Divider type="vertical" style={{ height: '100%' }} />
            <Flex flex={1} vertical>
              <Typography.Text extendClasses={(`fs-28x33 p-changePlan_cleanData${cleanRequire.storage.needToClean <= totalStorageCleaned ? '-cleaned' : '-notclean'}`)} fontweight="600">{totalStorageCleaned.toFixed(2)}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Cleaned</Typography.Text>
            </Flex>
          </Flex>
        </Flex>

        <Divider type="vertical" style={{ height: 'auto' }} />

        <Flex vertical flex={1} gap={8}>
          <Typography.Text>Number of files</Typography.Text>
          <Flex gap={16}>
            <Flex flex={1} vertical>
              <Typography.Text extendClasses="fs-28x33 " fontweight="600">{cleanRequire.file.needToClean}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Need to cleanup</Typography.Text>
            </Flex>

            <Divider type="vertical" style={{ height: '100%' }} />
            <Flex flex={1} vertical>
              <Typography.Text extendClasses={(`fs-28x33 p-changePlan_cleanData${cleanRequire.file.needToClean <= totalFileCleaned ? '-cleaned' : '-notclean'}`)} fontweight="600">{totalFileCleaned}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Cleaned</Typography.Text>
            </Flex>
          </Flex>
        </Flex>

        <Divider type="vertical" style={{ height: 'auto' }} />

        <Flex vertical flex={1} gap={8}>
          <Typography.Text>Number of web</Typography.Text>
          <Flex gap={16}>
            <Flex flex={1} vertical>
              <Typography.Text extendClasses="fs-28x33" fontweight="600">{cleanRequire.web.needToClean}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Need to cleanup</Typography.Text>
            </Flex>

            <Divider type="vertical" style={{ height: '100%' }} />
            <Flex flex={1} vertical>
              <Typography.Text extendClasses={(`fs-28x33 p-changePlan_cleanData${cleanRequire.web.needToClean <= totalWebCleaned ? '-cleaned' : '-notclean'}`)} fontweight="600">{totalWebCleaned}</Typography.Text>
              <Typography.Text extendClasses="fs-12x16">Cleaned</Typography.Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <div className="p-changePlan_cleanData_mt32">
        <Typography.Text extendClasses="fs-20x24" fontweight="600">
          Cleanup your data
        </Typography.Text>

      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="o-cleanData_tab"
      >
        {dataList.length > 0 && dataList.map((item) => (
          <Tabs.TabPane
            key={item.id}
            tab={(
              <div className="">
                <Typography.Text extendClasses="o-cleanData_tab_text">{item.labelText}</Typography.Text>
              </div>
            )}
          >
            {item.content}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default CleanData;
